/**
 * Oxydoréduction — logique pure (Boîte à outils, 1re → Tle, physique-chimie).
 *
 * Un couple oxydant / réducteur Ox/Red s'écrit avec une demi-équation
 * électronique, en milieu acide :  a Ox + h H⁺ + n e⁻ = b Red + w H₂O.
 * Méthode du programme de 1re, dans l'ordre : conserver l'élément autre que
 * O et H (coefficients a et b), l'oxygène avec H₂O, l'hydrogène avec H⁺, la
 * charge avec des électrons e⁻. Une réaction d'oxydoréduction met en jeu
 * l'oxydant d'un couple et le réducteur d'un autre : on multiplie les
 * demi-équations pour que les électrons cédés soient tous captés, on
 * additionne, on simplifie les espèces présentes des deux côtés.
 *
 * Notation : « = » pour une demi-équation, « → » pour une réaction. Tout est
 * en entiers ; les formules s'écrivent en Unicode (indices, exposants), sans
 * LaTeX ; `speak` les traduit en mots pour le lecteur d'écran.
 */

export type State = 's' | 'aq' | 'g' | 'l';

export interface Species {
  id: string;
  /** « MnO₄⁻ » */
  text: string;
  /** nom des listes (« ion permanganate ») */
  name: string;
  /** lu sans coefficient (« ion permanganate », « eau ») */
  one: string;
  /** lu avec un coefficient (« ions permanganate », « molécules d’eau ») */
  many: string;
  elements: Readonly<Record<string, number>>;
  charge: number;
  state: State;
  /** couleur en solution aqueuse, si l'espèce est colorée */
  color?: string;
}

const sp = (
  id: string,
  text: string,
  name: string,
  elements: Record<string, number>,
  charge: number,
  state: State,
  o: { one?: string; many?: string; color?: string } = {},
): Species => ({
  id,
  text,
  name,
  one: o.one ?? name,
  many: o.many ?? (name.startsWith('ion ') ? `ions ${name.slice(4)}` : name),
  elements,
  charge,
  state,
  ...(o.color ? { color: o.color } : {}),
});

const atoms = (de: string) => ({ many: `atomes ${de}` });
const molecules = (de: string) => ({ many: `molécules ${de}` });

export const SPECIES = {
  'Cu2+': sp('Cu2+', 'Cu²⁺', 'ion cuivre II', { Cu: 1 }, 2, 'aq', { color: 'bleu' }),
  Cu: sp('Cu', 'Cu', 'cuivre', { Cu: 1 }, 0, 's', atoms('de cuivre')),
  'Zn2+': sp('Zn2+', 'Zn²⁺', 'ion zinc', { Zn: 1 }, 2, 'aq'),
  Zn: sp('Zn', 'Zn', 'zinc', { Zn: 1 }, 0, 's', atoms('de zinc')),
  'Fe2+': sp('Fe2+', 'Fe²⁺', 'ion fer II', { Fe: 1 }, 2, 'aq', { color: 'vert pâle' }),
  'Fe3+': sp('Fe3+', 'Fe³⁺', 'ion fer III', { Fe: 1 }, 3, 'aq', {
    color: 'jaune-orangé (rouille)',
  }),
  Fe: sp('Fe', 'Fe', 'fer', { Fe: 1 }, 0, 's', atoms('de fer')),
  'Ag+': sp('Ag+', 'Ag⁺', 'ion argent', { Ag: 1 }, 1, 'aq'),
  Ag: sp('Ag', 'Ag', 'argent', { Ag: 1 }, 0, 's', atoms('d’argent')),
  'Al3+': sp('Al3+', 'Al³⁺', 'ion aluminium', { Al: 1 }, 3, 'aq'),
  Al: sp('Al', 'Al', 'aluminium', { Al: 1 }, 0, 's', atoms('d’aluminium')),
  'H+': sp('H+', 'H⁺', 'ion hydrogène', { H: 1 }, 1, 'aq'),
  H2: sp('H2', 'H₂', 'dihydrogène', { H: 2 }, 0, 'g', molecules('de dihydrogène')),
  I2: sp('I2', 'I₂', 'diiode', { I: 2 }, 0, 'aq', {
    ...molecules('de diiode'),
    color: 'jaune à brun (bleu foncé avec l’empois d’amidon)',
  }),
  'I-': sp('I-', 'I⁻', 'ion iodure', { I: 1 }, -1, 'aq'),
  S4O6: sp('S4O6', 'S₄O₆²⁻', 'ion tétrathionate', { S: 4, O: 6 }, -2, 'aq'),
  S2O3: sp('S2O3', 'S₂O₃²⁻', 'ion thiosulfate', { S: 2, O: 3 }, -2, 'aq'),
  MnO4: sp('MnO4', 'MnO₄⁻', 'ion permanganate', { Mn: 1, O: 4 }, -1, 'aq', { color: 'violet' }),
  'Mn2+': sp('Mn2+', 'Mn²⁺', 'ion manganèse II', { Mn: 1 }, 2, 'aq'),
  Cr2O7: sp('Cr2O7', 'Cr₂O₇²⁻', 'ion dichromate', { Cr: 2, O: 7 }, -2, 'aq', {
    color: 'orange',
  }),
  'Cr3+': sp('Cr3+', 'Cr³⁺', 'ion chrome III', { Cr: 1 }, 3, 'aq', { color: 'vert' }),
  H2O2: sp('H2O2', 'H₂O₂', 'peroxyde d’hydrogène (eau oxygénée)', { H: 2, O: 2 }, 0, 'aq', {
    one: 'peroxyde d’hydrogène',
    ...molecules('de peroxyde d’hydrogène'),
  }),
  H2O: sp('H2O', 'H₂O', 'eau', { H: 2, O: 1 }, 0, 'l', molecules('d’eau')),
  O2: sp('O2', 'O₂', 'dioxygène', { O: 2 }, 0, 'g', molecules('de dioxygène')),
  Cl2: sp('Cl2', 'Cl₂', 'dichlore', { Cl: 2 }, 0, 'g', {
    ...molecules('de dichlore'),
    color: 'jaune-vert pâle',
  }),
  'Cl-': sp('Cl-', 'Cl⁻', 'ion chlorure', { Cl: 1 }, -1, 'aq'),
  ClO: sp('ClO', 'ClO⁻', 'ion hypochlorite', { Cl: 1, O: 1 }, -1, 'aq'),
  NO3: sp('NO3', 'NO₃⁻', 'ion nitrate', { N: 1, O: 3 }, -1, 'aq'),
  NO: sp('NO', 'NO', 'monoxyde d’azote', { N: 1, O: 1 }, 0, 'g', molecules('de monoxyde d’azote')),
  SO4: sp('SO4', 'SO₄²⁻', 'ion sulfate', { S: 1, O: 4 }, -2, 'aq'),
  SO2: sp('SO2', 'SO₂', 'dioxyde de soufre', { S: 1, O: 2 }, 0, 'aq', {
    ...molecules('de dioxyde de soufre'),
  }),
  CO2: sp('CO2', 'CO₂', 'dioxyde de carbone', { C: 1, O: 2 }, 0, 'aq', {
    ...molecules('de dioxyde de carbone'),
  }),
  H2C2O4: sp('H2C2O4', 'H₂C₂O₄', 'acide oxalique', { C: 2, H: 2, O: 4 }, 0, 'aq', {
    ...molecules('d’acide oxalique'),
  }),
  C6H6O6: sp('C6H6O6', 'C₆H₆O₆', 'acide déhydroascorbique', { C: 6, H: 6, O: 6 }, 0, 'aq', {
    ...molecules('d’acide déhydroascorbique'),
  }),
  C6H8O6: sp('C6H8O6', 'C₆H₈O₆', 'acide ascorbique (vitamine C)', { C: 6, H: 8, O: 6 }, 0, 'aq', {
    one: 'acide ascorbique',
    ...molecules('d’acide ascorbique'),
  }),
} as const satisfies Record<string, Species>;

export type SpeciesId = keyof typeof SPECIES;

export interface Couple {
  id: string;
  ox: SpeciesId;
  red: SpeciesId;
  /** l'oxydant se propose comme réactif (mode Réaction) */
  oxUsable: boolean;
  /** le réducteur se propose comme réactif (mode Réaction) */
  redUsable: boolean;
}

const C = (ox: SpeciesId, red: SpeciesId, use: { ox?: boolean; red?: boolean } = {}): Couple => ({
  id: `${ox}/${red}`,
  ox,
  red,
  oxUsable: use.ox ?? true,
  redUsable: use.red ?? true,
});

export const COUPLES: readonly Couple[] = [
  C('Cu2+', 'Cu'),
  C('Zn2+', 'Zn'),
  C('Fe2+', 'Fe'),
  C('Fe3+', 'Fe2+'),
  C('Ag+', 'Ag'),
  // Al³⁺ ne se réduit pas en solution aqueuse
  C('Al3+', 'Al', { ox: false }),
  C('H+', 'H2'),
  C('I2', 'I-'),
  C('S4O6', 'S2O3'),
  C('MnO4', 'Mn2+'),
  C('Cr2O7', 'Cr3+'),
  // l'eau, réducteur : hors programme (réaction lente)
  C('H2O2', 'H2O', { red: false }),
  C('O2', 'H2O2'),
  C('O2', 'H2O', { red: false }),
  C('Cl2', 'Cl-'),
  C('ClO', 'Cl-'),
  C('NO3', 'NO'),
  // SO₄²⁻ n'oxyde pas en solution diluée
  C('SO4', 'SO2', { ox: false }),
  C('CO2', 'H2C2O4'),
  C('C6H6O6', 'C6H8O6'),
];

export const coupleById = (id: string) => COUPLES.find((c) => c.id === id) ?? COUPLES[0]!;

/* ---------- écriture ---------- */

const NB = String.fromCharCode(0xa0);
const STATE_TEXT: Record<State, string> = { s: '(s)', aq: '(aq)', g: '(g)', l: '(ℓ)' };

export type Sep = '=' | '→';

export const coupleText = (c: Couple) => `${SPECIES[c.ox].text}/${SPECIES[c.red].text}`;

/** « 8 H⁺(aq) », « MnO₄⁻(aq) », « 5 e⁻ » (coefficient 1 sous-entendu) */
export function term(coef: number, id: SpeciesId | 'e', states: boolean): string {
  const body =
    id === 'e' ? 'e⁻' : `${SPECIES[id].text}${states ? STATE_TEXT[SPECIES[id].state] : ''}`;
  return coef === 1 ? body : `${coef}${NB}${body}`;
}

/** « 1 électron », « 5 électrons » */
export const electrons = (n: number) => `${n}${NB}électron${n > 1 ? 's' : ''}`;

/** « I₂ capte 2 électrons », « 2 I⁻ cèdent 2 électrons » : le coefficient compte */
export function exchangeText(coef: number, id: SpeciesId, n: number, gives: boolean): string {
  const verb = gives ? (coef > 1 ? 'cèdent' : 'cède') : coef > 1 ? 'captent' : 'capte';
  return `${term(coef, id, false)} ${verb} ${electrons(n)}`;
}

/** « A », « A et B », « A, B et C » */
export const listText = (xs: readonly string[]) =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`;

/* ---------- demi-équation ---------- */

/** côté gauche (réactifs) en positif, droit en négatif ; e = électrons */
export type Side = Partial<Record<SpeciesId | 'e', number>>;

const gcd = (x: number, y: number): number => {
  x = Math.abs(x);
  y = Math.abs(y);
  while (y) [x, y] = [y, x % y];
  return x || 1;
};
const lcm = (x: number, y: number) => (x * y) / gcd(x, y);

export interface HalfEquation {
  couple: Couple;
  /** l'élément conservé en premier (« Mn »…) ; « O » ou « H » : pas d'étape élément */
  main: string;
  a: number;
  b: number;
  /** H₂O ajoutées : > 0 à droite, < 0 à gauche */
  w: number;
  /** H⁺ ajoutés : > 0 à gauche, < 0 à droite */
  h: number;
  /** électrons, à gauche (sens de la réduction) */
  n: number;
}

/** l'élément à conserver d'abord : ni O ni H, sinon O, sinon H */
export function mainElement(c: Couple): string {
  const all = [...Object.keys(SPECIES[c.ox].elements), ...Object.keys(SPECIES[c.red].elements)];
  return all.find((e) => e !== 'O' && e !== 'H') ?? (all.includes('O') ? 'O' : 'H');
}

const count = (id: SpeciesId, el: string) => SPECIES[id].elements[el] ?? 0;
const byMain = (he: HalfEquation) => he.main !== 'O' && he.main !== 'H';

/**
 * La demi-équation, sens de la réduction, coefficients entiers les plus
 * petits. Sans élément autre que O et H (H₂O₂, O₂, H⁺), a = b = 1 et l'eau
 * puis H⁺ font le reste, comme dans les manuels : H₂O₂ = H₂O + H₂O.
 */
export function halfEquation(c: Couple): HalfEquation {
  const main = mainElement(c);
  let a = 1;
  let b = 1;
  if (main !== 'O' && main !== 'H') {
    const L = lcm(count(c.ox, main), count(c.red, main));
    a = L / count(c.ox, main);
    b = L / count(c.red, main);
  }
  // O avec l'eau
  let w = a * count(c.ox, 'O') - b * count(c.red, 'O');
  // H avec H⁺
  const leftH = a * count(c.ox, 'H') + (w < 0 ? -2 * w : 0);
  const rightH = b * count(c.red, 'H') + (w > 0 ? 2 * w : 0);
  let h = rightH - leftH;
  // charge avec les électrons
  const leftQ = a * SPECIES[c.ox].charge + (h > 0 ? h : 0);
  const rightQ = b * SPECIES[c.red].charge + (h < 0 ? -h : 0);
  let n = leftQ - rightQ;
  const g = [a, b, w, h, n].reduce((x, y) => gcd(x, y));
  if (g > 1) [a, b, w, h, n] = [a / g, b / g, w / g, h / g, n / g];
  return { couple: c, main, a, b, w, h, n };
}

/** la demi-équation en vecteur signé (gauche > 0, droite < 0) ; H₂O et H⁺ s'additionnent */
export function halfVector(he: HalfEquation): Side {
  const v: Side = {};
  const put = (id: SpeciesId | 'e', k: number) => {
    if (!k) return;
    const x = (v[id] ?? 0) + k;
    if (x) v[id] = x;
    else delete v[id];
  };
  put(he.couple.ox, he.a);
  put(he.couple.red, -he.b);
  put('H2O', -he.w);
  put('H+', he.h);
  put('e', he.n);
  return v;
}

/** ordre des termes d'un côté : les espèces, puis H⁺, H₂O, e⁻ */
const ORDER = (k: string) => (k === 'e' ? 3 : k === 'H2O' ? 2 : k === 'H+' ? 1 : 0);

/** écrit un vecteur signé : « gauche = droite » ou « réactifs → produits » */
export function sideText(v: Side, states = true, sep: Sep = '='): string {
  const keys = (Object.keys(v) as Array<SpeciesId | 'e'>).sort((x, y) => ORDER(x) - ORDER(y));
  const left = keys.filter((k) => (v[k] ?? 0) > 0).map((k) => term(v[k]!, k, states));
  const right = keys.filter((k) => (v[k] ?? 0) < 0).map((k) => term(-v[k]!, k, states));
  return `${left.join(' + ') || '0'} ${sep} ${right.join(' + ') || '0'}`;
}

export const halfText = (he: HalfEquation, states = true) => sideText(halfVector(he), states);

export interface Step {
  title: string;
  /** l'équation à ce stade */
  eq: string;
  say: string;
}

const plural = (k: number, word: string) => `${k}${NB}${word}${k > 1 ? 's' : ''}`;

/** les étapes de la méthode, avec l'équation (additive) à chaque étape */
export function halfSteps(c: Couple, states = true): Step[] {
  const he = halfEquation(c);
  const ox = SPECIES[c.ox];
  const red = SPECIES[c.red];
  const at = (a: number, b: number, w: number, h: number, n: number) =>
    halfVector({ ...he, a, b, w, h, n });
  const out: Step[] = [
    {
      title: 'Le couple',
      eq: sideText(at(1, 1, 0, 0, 0), states),
      say: `On écrit l’oxydant à gauche, le réducteur à droite : ${ox.text} = ${red.text}.`,
    },
  ];
  if (byMain(he)) {
    const k = he.a * count(c.ox, he.main);
    const coefs = [
      he.a > 1 ? `${he.a} devant ${ox.text}` : '',
      he.b > 1 ? `${he.b} devant ${red.text}` : '',
    ].filter(Boolean);
    out.push({
      title: `L’élément ${he.main}`,
      eq: sideText(at(he.a, he.b, 0, 0, 0), states),
      say:
        coefs.length === 0
          ? `L’élément ${he.main} est déjà conservé.`
          : `${plural(k, 'atome')} ${he.main} de chaque côté : coefficient ${listText(coefs)}.`,
    });
  }
  const leftO = he.a * count(c.ox, 'O');
  const rightO = he.b * count(c.red, 'O');
  out.push({
    title: 'L’oxygène avec H₂O',
    eq: sideText(at(he.a, he.b, he.w, 0, 0), states),
    say:
      he.w === 0
        ? leftO === 0
          ? 'Pas d’oxygène : rien à faire.'
          : `${plural(leftO, 'atome')} O de chaque côté : l’oxygène est déjà conservé.`
        : `${plural(leftO, 'atome')} O à gauche, ${rightO} à droite : on ajoute ${term(Math.abs(he.w), 'H2O', false)} ${he.w > 0 ? 'à droite' : 'à gauche'}.`,
  });
  const leftH = he.a * count(c.ox, 'H') + (he.w < 0 ? -2 * he.w : 0);
  const rightH = he.b * count(c.red, 'H') + (he.w > 0 ? 2 * he.w : 0);
  out.push({
    title: 'L’hydrogène avec H⁺',
    eq: sideText(at(he.a, he.b, he.w, he.h, 0), states),
    say:
      he.h === 0
        ? leftH === 0
          ? 'Pas d’hydrogène : rien à faire.'
          : `${plural(leftH, 'atome')} H de chaque côté : l’hydrogène est déjà conservé.`
        : c.ox === 'H+'
          ? `${plural(leftH, 'atome')} H à gauche, ${rightH} à droite : coefficient ${leftH + he.h} devant H⁺.`
          : `${plural(leftH, 'atome')} H à gauche, ${rightH} à droite : on ajoute ${term(Math.abs(he.h), 'H+', false)} ${he.h > 0 ? 'à gauche' : 'à droite'} (milieu acide).`,
  });
  const q = balance(at(he.a, he.b, he.w, he.h, 0)).rows.at(-1)!;
  out.push({
    title: 'La charge avec des électrons',
    eq: sideText(at(he.a, he.b, he.w, he.h, he.n), states),
    say: `Charge ${chargeText(q.left)} à gauche, ${chargeText(q.right)} à droite : on ajoute ${electrons(he.n)} à gauche (l’oxydant capte des électrons).`,
  });
  return out;
}

/** « +7 », « −1 », « 0 » */
export const chargeText = (q: number) => (q > 0 ? `+${q}` : q < 0 ? `−${-q}` : '0');

/* ---------- conservation (mode « À toi ») ---------- */

export interface Balance {
  rows: Array<{ label: string; left: number; right: number; ok: boolean }>;
  ok: boolean;
}

/** bilan élément par élément et charge d'un vecteur signé (la charge en dernier) */
export function balance(v: Side): Balance {
  const els = new Set<string>();
  for (const k of Object.keys(v))
    if (k !== 'e') for (const e of Object.keys(SPECIES[k as SpeciesId].elements)) els.add(e);
  const order = [...els].sort(
    (x, y) => (x === 'O' ? 1 : x === 'H' ? 2 : 0) - (y === 'O' ? 1 : y === 'H' ? 2 : 0),
  );
  const tally = (side: 1 | -1, f: (k: SpeciesId | 'e') => number) =>
    (Object.keys(v) as Array<SpeciesId | 'e'>)
      .filter((k) => Math.sign(v[k]!) === side)
      .reduce((s, k) => s + Math.abs(v[k]!) * f(k), 0);
  const rows = order.map((e) => {
    const left = tally(1, (k) => (k === 'e' ? 0 : count(k, e)));
    const right = tally(-1, (k) => (k === 'e' ? 0 : count(k, e)));
    return { label: e, left, right, ok: left === right };
  });
  const ql = tally(1, (k) => (k === 'e' ? -1 : SPECIES[k].charge));
  const qr = tally(-1, (k) => (k === 'e' ? -1 : SPECIES[k].charge));
  rows.push({ label: 'charge', left: ql, right: qr, ok: ql === qr });
  return { rows, ok: rows.every((r) => r.ok) };
}

/** v = m × cible ? (même côté, mêmes espèces) */
export function sameUpTo(v: Side, target: Side, m: number): boolean {
  const keys = new Set([...Object.keys(v), ...Object.keys(target)]) as Set<SpeciesId | 'e'>;
  for (const k of keys) if ((v[k] ?? 0) !== m * (target[k] ?? 0)) return false;
  return true;
}

/* ---------- équation de la réaction ---------- */

export interface Reaction {
  /** couple dont l'OXYDANT réagit (il est réduit) */
  A: HalfEquation;
  /** couple dont le RÉDUCTEUR réagit (il est oxydé) */
  B: HalfEquation;
  kA: number;
  kB: number;
  /** électrons échangés (kA·nA = kB·nB) */
  N: number;
  /** somme brute (électrons compris, avant simplification) : réactifs et produits */
  raw: { left: Side; right: Side };
  /** espèces présentes des deux côtés de la somme brute (hors e⁻) */
  common: SpeciesId[];
  /** diviseur commun retiré après simplification (1 le plus souvent) */
  div: number;
  /** équation simplifiée, vecteur signé */
  net: Side;
  /** l'oxydant et le réducteur choisis restent des réactifs */
  ok: boolean;
}

const scale = (v: Side, k: number): Side => {
  const s: Side = {};
  for (const [key, x] of Object.entries(v) as Array<[SpeciesId | 'e', number]>) s[key] = x * k;
  return s;
};

/** somme de vecteurs signés, électrons retirés si demandé, termes nuls supprimés */
function addSides(parts: readonly Side[], dropElectrons = false): Side {
  const v: Side = {};
  for (const p of parts)
    for (const [k, x] of Object.entries(p) as Array<[SpeciesId | 'e', number]>)
      v[k] = (v[k] ?? 0) + x;
  if (dropElectrons) delete v.e;
  for (const k of Object.keys(v) as Array<SpeciesId | 'e'>) if (!v[k]) delete v[k];
  return v;
}

/** Ox de A + Red de B : les électrons captés = les électrons cédés */
export function reaction(coupleA: Couple, coupleB: Couple): Reaction {
  const A = halfEquation(coupleA);
  const B = halfEquation(coupleB);
  const g = gcd(A.n, B.n);
  const kA = B.n / g;
  const kB = A.n / g;
  const vA = halfVector(A);
  const vB = halfVector(B);
  // somme brute : chaque terme garde son côté (B est écrite dans le sens de l'oxydation)
  const left: Side = {};
  const right: Side = {};
  const add = (s: Side, k: SpeciesId | 'e', x: number) => {
    s[k] = (s[k] ?? 0) + x;
  };
  for (const [k, x] of Object.entries(vA) as Array<[SpeciesId | 'e', number]>)
    add(x > 0 ? left : right, k, Math.abs(x) * kA);
  for (const [k, x] of Object.entries(vB) as Array<[SpeciesId | 'e', number]>)
    add(x > 0 ? right : left, k, Math.abs(x) * kB);
  const common = (Object.keys(left) as Array<SpeciesId | 'e'>).filter(
    (k): k is SpeciesId => k !== 'e' && right[k] !== undefined,
  );
  const net: Side = {};
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]) as Set<SpeciesId | 'e'>;
  for (const k of keys) {
    const x = (left[k] ?? 0) - (right[k] ?? 0);
    if (x) net[k] = x;
  }
  const div = Object.values(net).reduce((s, x) => gcd(s, x!), 0) || 1;
  if (div > 1) for (const k of Object.keys(net) as Array<SpeciesId | 'e'>) net[k] = net[k]! / div;
  const ok = (net[coupleA.ox] ?? 0) > 0 && (net[coupleB.red] ?? 0) > 0;
  return { A, B, kA, kB, N: A.n * kA, raw: { left, right }, common, div, net, ok };
}

/** l'équation simplifiée, « … → … » */
export const netText = (r: Reaction, states = true) => sideText(r.net, states, '→');

/** la somme brute, « … + 5 e⁻ + … = … + 5 e⁻ » (somme de demi-équations : « = ») */
export function rawText(r: Reaction, states = true): string {
  const t = (s: Side) =>
    (Object.keys(s) as Array<SpeciesId | 'e'>)
      .sort((x, y) => ORDER(x) - ORDER(y))
      .map((k) => term(s[k]!, k, states))
      .join(' + ');
  return `${t(r.raw.left)} = ${t(r.raw.right)}`;
}

/** une demi-équation multipliée, écrite dans le sens où elle se produit */
export function scaledHalfText(
  he: HalfEquation,
  k: number,
  sense: 'reduction' | 'oxidation',
  states = true,
) {
  return sideText(scale(halfVector(he), sense === 'reduction' ? k : -k), states);
}

/** coefficient d'une espèce dans une demi-équation (valeur absolue) */
export const coefIn = (he: HalfEquation, id: SpeciesId) => Math.abs(halfVector(he)[id] ?? 0);

/** « On multiplie la réduction par 1 et l'oxydation par 5 : … » */
export function multiplyText(r: Reaction): string {
  const N = r.N;
  const ex = `${electrons(N)} capté${N > 1 ? 's' : ''}, autant de cédé${N > 1 ? 's' : ''}`;
  if (r.kA === 1 && r.kB === 1)
    return `Les deux demi-équations échangent ${electrons(N)} : on les additionne telles quelles, puis on simplifie.`;
  const parts = [
    r.kA > 1 ? `la réduction par ${r.kA}` : '',
    r.kB > 1 ? `l’oxydation par ${r.kB}` : '',
  ].filter(Boolean);
  return `On multiplie ${listText(parts)} : ${ex}. On additionne, puis on simplifie.`;
}

/** relation à l'équivalence d'un titrage : n(Ox)/a = n(Red)/b */
export function equivalence(r: Reaction): { ox: SpeciesId; red: SpeciesId; a: number; b: number } {
  return {
    ox: r.A.couple.ox,
    red: r.B.couple.red,
    a: r.net[r.A.couple.ox] ?? 0,
    b: r.net[r.B.couple.red] ?? 0,
  };
}

export const ratioText = (ox: SpeciesId, p: number, red: SpeciesId, q: number) =>
  `n(${SPECIES[ox].text}) / ${p} = n(${SPECIES[red].text}) / ${q}`;

/** espèces colorées en solution d'une réaction : « MnO₄⁻ violet » */
export function colorsOf(r: Reaction): string[] {
  const ids = [r.A.couple.ox, r.A.couple.red, r.B.couple.red, r.B.couple.ox];
  return ids
    .filter((id, i) => ids.indexOf(id) === i)
    .filter((id) => 'color' in SPECIES[id])
    .map((id) => `${SPECIES[id].text} ${(SPECIES[id] as Species).color}`);
}

/* ---------- lecture à voix haute ---------- */

const SAID_STATE: Record<string, string> = {
  '(s)': ' solide',
  '(aq)': ' en solution',
  '(g)': ' gazeux',
  '(ℓ)': ' liquide',
};
const singular = (many: string) => many.replace(/^(\S+?)s /, '$1 ');
const FORMULAS = [
  ...Object.values(SPECIES).map((s) => [s.text, s] as const),
  ['e⁻', null] as const,
].sort((x, y) => y[0].length - x[0].length);
const FORMULA_RE = new RegExp(
  String.raw`(?<![\p{L}\p{N}⁺⁻])(?:(\d+)[ \u00a0])?(` +
    FORMULAS.map(([t]) => t).join('|') +
    String.raw`)(\((?:s|aq|g|ℓ)\))?(?![\p{L}\p{N}⁺⁻])`,
  'gu',
);

/**
 * Texte lisible par le lecteur d'écran : formules en mots (« 8 ions
 * hydrogène »), « + » → « plus », « = » → « égale », « → » → « donnent »,
 * « / » → « sur », n(X) → « n de X ».
 */
export function speak(text: string): string {
  return text
    .replace(FORMULA_RE, (_m, coef: string | undefined, f: string, st: string | undefined) => {
      const s = FORMULAS.find(([t]) => t === f)![1];
      const k = coef === undefined ? null : Number(coef);
      const body =
        s === null
          ? k === null || k === 1
            ? `${k === 1 ? '1' : 'un'} électron`
            : `${k} électrons`
          : k === null
            ? s.one
            : k === 1
              ? `1 ${singular(s.many)}`
              : `${k} ${s.many}`;
      return `${body}${st ? (SAID_STATE[st] ?? '') : ''}`;
    })
    .replace(/n\(([^()]*)\)/g, (_m, x: string) =>
      /^[aeiouhéèêà]/i.test(x) ? `n d’${x}` : `n de ${x}`,
    )
    .replace(/\s\+\s/g, ' plus ')
    .replace(/\s=\s/g, ' égale ')
    .replace(/\s→\s/g, ' donnent ')
    .replace(/\s?\/\s?/g, ' sur ')
    .replace(/\+(?=\d)/g, 'plus ')
    .replace(/−(?=\d)/g, 'moins ')
    .replace(/\s×\s/g, ' fois ');
}

/* ---------- réactions classiques ---------- */

export interface ClassicPair {
  /** couple dont l'oxydant réagit */
  a: string;
  /** couple dont le réducteur réagit */
  b: string;
  label: string;
  /** support d'un titrage de 1re */
  titration?: boolean;
  note?: string;
  /** hors défi (dismutation : l'oxydant et le réducteur sont la même espèce) */
  noQuiz?: boolean;
}

export const CLASSIC_PAIRS: readonly ClassicPair[] = [
  {
    a: 'MnO4/Mn2+',
    b: 'Fe3+/Fe2+',
    label: 'ions permanganate et fer II (titrage)',
    titration: true,
  },
  { a: 'Cu2+/Cu', b: 'Zn2+/Zn', label: 'ions cuivre II et zinc' },
  { a: 'I2/I-', b: 'S4O6/S2O3', label: 'diiode et ions thiosulfate (titrage)', titration: true },
  { a: 'Ag+/Ag', b: 'Cu2+/Cu', label: 'ions argent et cuivre' },
  {
    a: 'MnO4/Mn2+',
    b: 'CO2/H2C2O4',
    label: 'ions permanganate et acide oxalique',
    titration: true,
  },
  { a: 'H2O2/H2O', b: 'I2/I-', label: 'eau oxygénée et ions iodure' },
  { a: 'Cr2O7/Cr3+', b: 'Fe3+/Fe2+', label: 'ions dichromate et fer II', titration: true },
  {
    a: 'I2/I-',
    b: 'C6H6O6/C6H8O6',
    label: 'diiode et vitamine C (titrage)',
    titration: true,
  },
  { a: 'H+/H2', b: 'Zn2+/Zn', label: 'acide et zinc' },
  {
    a: 'MnO4/Mn2+',
    b: 'O2/H2O2',
    label: 'ions permanganate et eau oxygénée (titrage)',
    titration: true,
  },
  {
    a: 'I2/I-',
    b: 'SO4/SO2',
    label: 'diiode et dioxyde de soufre (dosage du vin)',
    titration: true,
  },
  { a: 'NO3/NO', b: 'Cu2+/Cu', label: 'acide nitrique et cuivre' },
  { a: 'ClO/Cl-', b: 'I2/I-', label: 'eau de Javel et ions iodure' },
  { a: 'H+/H2', b: 'Fe2+/Fe', label: 'acide et fer' },
  {
    a: 'ClO/Cl-',
    b: 'Cl2/Cl-',
    label: 'eau de Javel et acide chlorhydrique',
    note: 'Ne jamais mélanger eau de Javel et acide : il se dégage du dichlore Cl₂, un gaz toxique.',
  },
  {
    a: 'H2O2/H2O',
    b: 'O2/H2O2',
    label: 'dismutation de l’eau oxygénée',
    note: 'H₂O₂ est à la fois l’oxydant d’un couple et le réducteur de l’autre : c’est une dismutation.',
    noQuiz: true,
  },
];

export const classicLabel = (p: ClassicPair) =>
  `${SPECIES[coupleById(p.a).ox].text} + ${SPECIES[coupleById(p.b).red].text} — ${p.label}`;

/* ---------- défi ---------- */

export type QuizKind = 'half' | 'identify' | 'reaction' | 'equivalence';
export const QUIZ_KINDS: readonly QuizKind[] = [
  'half',
  'identify',
  'half',
  'reaction',
  'equivalence',
];

export interface QuizOption {
  text: string;
  correct: boolean;
  hint?: string;
  /** nature du distracteur (tests) */
  tag?: string;
}

export interface Quiz {
  kind: QuizKind;
  question: string;
  options: QuizOption[];
  explain: string;
}

const pick = <T>(xs: readonly T[], rnd: () => number): T => xs[Math.floor(rnd() * xs.length)]!;

function shuffle<T>(xs: readonly T[], rnd: () => number): T[] {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

const uniq = (opts: QuizOption[]) =>
  opts.filter((o, i) => opts.findIndex((x) => x.text === o.text) === i);

/** couples dont la demi-équation demande H₂O ou H⁺ (les plus formateurs) */
export const RICH = COUPLES.filter((c) => {
  const he = halfEquation(c);
  return he.w !== 0 || he.h !== 0;
});

/** les distracteurs d'une demi-équation, chacun avec un indice qui dit vrai */
export function halfDistractors(c: Couple, states = false): QuizOption[] {
  const he = halfEquation(c);
  const out: QuizOption[] = [
    {
      text: sideText(halfVector({ ...he, n: -he.n }), states),
      correct: false,
      tag: 'flip',
      hint: 'Les électrons sont du côté de l’oxydant, qui les capte : ici, la charge n’est pas conservée.',
    },
    {
      text: sideText(halfVector({ ...he, w: he.w === 0 ? he.a : he.w * 2 }), states),
      correct: false,
      tag: 'water',
      hint:
        he.w === 0
          ? 'L’oxygène est déjà conservé : pas besoin d’eau.'
          : 'Compte les atomes d’oxygène : on ajoute une molécule H₂O par atome O manquant, pas plus.',
    },
  ];
  if (he.h !== 0)
    out.splice(1, 0, {
      text: sideText(halfVector({ ...he, h: 0 }), states),
      correct: false,
      tag: 'noH',
      hint: 'Il manque les ions H⁺ : l’hydrogène et la charge ne sont pas conservés.',
    });
  return out;
}

/** les distracteurs de l'équation d'une réaction : sans électrons, indices vrais */
export function reactionDistractors(r: Reaction, states = false): QuizOption[] {
  const vA = halfVector(r.A);
  const vB = halfVector(r.B);
  const ox = SPECIES[r.A.couple.ox].text;
  const red = SPECIES[r.B.couple.red].text;
  const t = (v: Side) => sideText(v, states, '→');
  const out: QuizOption[] = [];
  const push = (v: Side, tag: string, hint: string) =>
    out.push({ text: t(v), correct: false, tag, hint });
  if (r.A.n !== r.B.n)
    push(
      addSides([vA, scale(vB, -1)], true),
      'electrons',
      `La réduction met en jeu ${electrons(r.A.n)}, l’oxydation ${electrons(r.B.n)} : on multiplie les demi-équations pour qu’il y en ait autant, sinon la charge n’est pas conservée.`,
    );
  if (r.kA !== r.kB)
    push(
      addSides([scale(vA, r.kB), scale(vB, -r.kA)], true),
      'swapped',
      'Multiplicateurs inversés : on multiplie chaque demi-équation par le nombre d’électrons de l’autre. Ici, la charge n’est pas conservée.',
    );
  const H = r.net['H+'];
  if (H && r.A.couple.ox !== 'H+') {
    const noH: Side = { ...r.net };
    delete noH['H+'];
    push(
      noH,
      'noH',
      H > 0
        ? 'Il manque les ions H⁺ (milieu acide) : l’hydrogène et la charge ne sont pas conservés.'
        : 'Il manque les ions H⁺ formés : l’hydrogène et la charge ne sont pas conservés.',
    );
    push(
      { ...r.net, 'H+': -H },
      'hSide',
      'Les ions H⁺ ne sont pas du bon côté : l’hydrogène et la charge ne sont pas conservés.',
    );
  }
  const W = r.net.H2O;
  const usesWater = [r.A.couple.ox, r.A.couple.red, r.B.couple.ox, r.B.couple.red].includes('H2O');
  if (W && !usesWater) {
    const noW: Side = { ...r.net };
    delete noW.H2O;
    push(noW, 'noWater', 'Il manque l’eau : l’oxygène et l’hydrogène ne sont pas conservés.');
  }
  push(
    addSides([scale(vA, r.kA), scale(vB, r.kB)], true),
    'twoRed',
    `${SPECIES[r.B.couple.ox].text} est un oxydant : il capte des électrons, il n’en cède pas. Le réducteur qui réagit est ${red}.`,
  );
  push(
    addSides([scale(vA, -r.kA), scale(vB, -r.kB)], true),
    'twoOx',
    `${SPECIES[r.A.couple.red].text} est un réducteur : il cède des électrons, il n’en capte pas. L’oxydant qui réagit est ${ox}.`,
  );
  push(
    scale(r.net, -1),
    'reversed',
    `C’est la réaction inverse : ici, ${ox} et ${red} sont les réactifs, à gauche de la flèche.`,
  );
  const good = netText(r, states);
  return uniq(out).filter((o) => o.text !== good);
}

/** les distracteurs de la relation à l'équivalence (aucun n'est une forme juste) */
export function equivalenceDistractors(r: Reaction): QuizOption[] {
  const { ox, red, a, b } = equivalence(r);
  // deux relations de même rapport sont la même relation : la juste, et chaque distracteur, une fois
  const kept: Array<[number, number]> = [[a, b]];
  const out: QuizOption[] = [];
  const push = (p: number, q: number, tag: string, hint: string) => {
    if (kept.some(([x, y]) => p * y === q * x)) return;
    kept.push([p, q]);
    out.push({ text: ratioText(ox, p, red, q), correct: false, tag, hint });
  };
  push(
    b,
    a,
    'swapped',
    `Chaque quantité de matière se divise par son propre coefficient : ${a} pour ${SPECIES[ox].text}, ${b} pour ${SPECIES[red].text}.`,
  );
  push(
    1,
    1,
    'equal',
    `Les coefficients ne sont pas égaux : ${a} pour ${SPECIES[ox].text}, ${b} pour ${SPECIES[red].text}.`,
  );
  for (const [k, x] of Object.entries(r.net) as Array<[SpeciesId, number]>) {
    if (k === ox || k === red || k === 'H2O') continue;
    const c = Math.abs(x);
    const role = x > 0 ? 'un réactif' : 'un produit';
    push(
      a,
      c,
      `other-${k}`,
      `${c} est le coefficient de ${SPECIES[k].text} (${role}) : on prend celui de ${SPECIES[red].text}, ${b}.`,
    );
    push(
      c,
      b,
      `other-${k}`,
      `${c} est le coefficient de ${SPECIES[k].text} (${role}) : on prend celui de ${SPECIES[ox].text}, ${a}.`,
    );
  }
  return out;
}

/** Une question du défi ; `rnd` décide du couple et de l'ordre (même graine, même question) */
export function makeQuiz(kind: QuizKind, rnd: () => number, states = false): Quiz {
  if (kind === 'half') {
    const c = pick(RICH, rnd);
    const he = halfEquation(c);
    const opts = uniq([
      { text: halfText(he, states), correct: true },
      ...halfDistractors(c, states),
    ]);
    return {
      kind,
      question: `Quelle est la demi-équation électronique du couple ${coupleText(c)} (milieu acide) ?`,
      options: shuffle(opts.slice(0, 4), rnd),
      explain: halfSteps(c, states)
        .slice(1)
        .map((s) => s.say)
        .join(' '),
    };
  }
  const pool = CLASSIC_PAIRS.filter((p) => !p.noQuiz && (kind !== 'equivalence' || p.titration));
  const pair = pick(pool, rnd);
  const A = coupleById(pair.a);
  const B = coupleById(pair.b);
  const r = reaction(A, B);
  const ox = SPECIES[A.ox].text;
  const red = SPECIES[B.red].text;
  if (kind === 'identify') {
    const askOx = rnd() < 0.5;
    const opts: QuizOption[] = [
      {
        text: ox,
        correct: askOx,
        ...(askOx ? {} : { hint: `${ox} capte des électrons : c’est l’oxydant.` }),
      },
      {
        text: red,
        correct: !askOx,
        ...(!askOx ? {} : { hint: `${red} cède des électrons : c’est le réducteur.` }),
      },
      {
        text: SPECIES[A.red].text,
        correct: false,
        hint: `${SPECIES[A.red].text} est un produit : c’est le réducteur conjugué de ${ox}, formé par la réaction.`,
      },
      {
        text: SPECIES[B.ox].text,
        correct: false,
        hint: `${SPECIES[B.ox].text} est un produit : c’est l’oxydant conjugué de ${red}, formé par la réaction.`,
      },
    ];
    return {
      kind,
      question: `Dans la réaction ${netText(r, states)}, quel est ${askOx ? 'l’oxydant' : 'le réducteur'} ?`,
      options: shuffle(uniq(opts), rnd),
      explain: `Couples ${coupleText(A)} et ${coupleText(B)} : ${ox} (oxydant) est réduit en ${SPECIES[A.red].text}, ${red} (réducteur) est oxydé en ${SPECIES[B.ox].text}.`,
    };
  }
  if (kind === 'equivalence') {
    const { a, b } = equivalence(r);
    const wrong = equivalenceDistractors(r);
    const first = wrong.filter((o) => o.tag === 'swapped');
    const rest = shuffle(
      wrong.filter((o) => o.tag !== 'swapped'),
      rnd,
    );
    const opts = [
      { text: ratioText(A.ox, a, B.red, b), correct: true },
      ...[...first, ...rest].slice(0, 3),
    ];
    return {
      kind,
      question: `Titrage : ${ox} réagit avec ${red} selon ${netText(r, states)}. Quelle relation est vraie à l’équivalence ?`,
      options: shuffle(opts, rnd),
      explain: `À l’équivalence, les réactifs ont été introduits dans les proportions stœchiométriques : chaque quantité de matière se divise par son coefficient dans l’équation (${a} pour ${ox}, ${b} pour ${red}).`,
    };
  }
  // équation de la réaction
  const wrong = reactionDistractors(r, states);
  const first = wrong.filter((o) => o.tag === 'electrons');
  const rest = shuffle(
    wrong.filter((o) => o.tag !== 'electrons'),
    rnd,
  );
  const opts = [{ text: netText(r, states), correct: true }, ...[...first, ...rest].slice(0, 3)];
  return {
    kind,
    question: `Quelle est l’équation de la réaction entre ${ox} et ${red} (couples ${coupleText(A)} et ${coupleText(B)}) ?`,
    options: shuffle(opts, rnd),
    explain: multiplyText(r),
  };
}
